/**
 * useReportNetworkIdle Hook
 *
 * This hook monitors network activity to determine when all API requests, charts, and images
 * have finished loading. It's designed specifically for PDF generation with Gotenberg,
 * ensuring that the PDF is only generated after all content is fully loaded.
 *
 * How it works:
 * 1. Intercepts all fetch() and XMLHttpRequest calls to track active network requests
 * 2. Monitors React Query cache for pending queries
 * 3. Checks if Highcharts containers are fully rendered
 * 4. Verifies all images are loaded
 * 5. Ensures document is in 'complete' state
 * 6. Sets window.reportPdfGeneration = 'ready' when everything is loaded
 *
 * Important Notes:
 * - This hook modifies global window.fetch and XMLHttpRequest.prototype
 * - These modifications are cleaned up when the component unmounts
 * - Only use this hook in the GenerateReports component (PDF generation page)
 * - The backend (Gotenberg) should use waitForExpression: 'window.reportPdfGeneration === "ready"'
 *   to wait for the ready signal before generating the PDF
 *
 * @param timeout - Time in milliseconds to wait after network appears idle before confirming (default: 500ms)
 * @returns boolean - true when network is idle and all content is loaded, false otherwise
 */

import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const useReportNetworkIdle = (timeout: number = 2000): boolean => {
  const [isNetworkIdle, setIsNetworkIdle] = useState(false);
  const queryClient = useQueryClient();
  const activeRequestsRef = useRef<Set<number>>(new Set());
  const requestIdCounterRef = useRef(0);
  const isNetworkIdleStateRef = useRef(false);
  const isMountedRef = useRef(true);
  const originalFetchRef = useRef<typeof window.fetch | null>(null);
  const originalXHROpenRef = useRef<
    typeof XMLHttpRequest.prototype.open | null
  >(null);
  const originalXHRSendRef = useRef<
    typeof XMLHttpRequest.prototype.send | null
  >(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Track active fetch requests
    const originalFetch = window.fetch;
    originalFetchRef.current = originalFetch;

    const fetchInterceptor = (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> => {
      const requestId = ++requestIdCounterRef.current;
      activeRequestsRef.current.add(requestId);

      return originalFetch(input, init)
        .then((response) => {
          activeRequestsRef.current.delete(requestId);
          return response;
        })
        .catch((error) => {
          activeRequestsRef.current.delete(requestId);
          throw error;
        });
    };

    // Track active XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    // Store originals immediately to prevent race conditions
    originalXHROpenRef.current = originalXHROpen;
    originalXHRSendRef.current = originalXHRSend;

    (XMLHttpRequest.prototype as any).open = function (
      ...args: Parameters<typeof XMLHttpRequest.prototype.open>
    ) {
      const xhr = this as any;
      const requestId = ++requestIdCounterRef.current;
      xhr.__requestId = requestId;
      activeRequestsRef.current.add(requestId);
      return originalXHROpen.apply(this, args);
    };

    (XMLHttpRequest.prototype as any).send = function (
      ...args: Parameters<typeof XMLHttpRequest.prototype.send>
    ) {
      const xhr = this as any;
      const requestId = xhr.__requestId;
      if (!requestId) {
        return originalXHRSend.apply(this, args);
      }

      // Track completion and ensure we only delete once
      let completed = false;
      const cleanup = () => {
        if (!completed) {
          completed = true;
          activeRequestsRef.current.delete(requestId);
        }
      };

      // Primary tracking via onreadystatechange
      const originalOnReadyStateChange = this.onreadystatechange;
      this.onreadystatechange = function (...cbArgs: any[]) {
        if (this.readyState === XMLHttpRequest.DONE) {
          cleanup();
        }
        if (originalOnReadyStateChange) {
          return originalOnReadyStateChange.apply(this, cbArgs);
        }
      };

      // Backup tracking via event listeners (using once: true to auto-cleanup)
      this.addEventListener('loadend', cleanup, { once: true });
      this.addEventListener('error', cleanup, { once: true });
      this.addEventListener('abort', cleanup, { once: true });

      return originalXHRSend.apply(this, args);
    };

    // Install interceptors
    window.fetch = fetchInterceptor as typeof window.fetch;

    const checkNetworkIdle = () => {
      // Don't run if component is unmounted
      if (!isMountedRef.current) {
        return;
      }

      // Check active tracked requests (fetch/XHR)
      const hasActiveTrackedRequests = activeRequestsRef.current.size > 0;

      // Check performance API for any pending requests (as backup)
      // More accurate check: pending requests have responseEnd = 0 AND are recent
      const resources = window.performance.getEntriesByType(
        'resource'
      ) as PerformanceResourceTiming[];
      const now = performance.now();
      const pendingPerformanceRequests = resources.filter((resource) => {
        // A request is pending if:
        // 1. responseEnd is 0 (not completed) AND
        // 2. startTime is set (request has started) AND
        // 3. It started recently (within last 60 seconds to avoid stale entries)
        return (
          resource.responseEnd === 0 &&
          resource.startTime > 0 &&
          resource.startTime > now - 60000
        );
      });

      // Check React Query states — use fetchStatus to detect queries actively making network requests.
      // status === 'pending' includes queries that have no data but aren't fetching
      // (e.g. disabled/paused queries from unrelated global components like bulk_activities).
      const queries = queryClient.getQueryCache().getAll();
      const hasActiveQueries = queries.some(
        (query) => query.state.fetchStatus === 'fetching'
      );

      // Check if charts are rendered
      const chartContainers = document.querySelectorAll(
        '.highcharts-container'
      );
      const chartsReady =
        chartContainers.length === 0 ||
        Array.from(chartContainers).every((container) => {
          const svg = container.querySelector('svg');
          return svg && svg.children.length > 0;
        });

      // Check if images are loaded
      const images = document.querySelectorAll('img');
      const imagesLoaded = Array.from(images).every((img) => img.complete);

      // Check if document is fully loaded
      const documentReady = document.readyState === 'complete';

      const isFullyIdle =
        !hasActiveTrackedRequests &&
        pendingPerformanceRequests.length === 0 &&
        !hasActiveQueries &&
        chartsReady &&
        imagesLoaded &&
        documentReady;

      if (isFullyIdle) {
        // If everything is ready, wait for a timeout period to ensure stability
        timeoutId = setTimeout(() => {
          // Don't run if component is unmounted
          if (!isMountedRef.current) {
            return;
          }

          // Double-check all conditions one more time
          const finalHasActiveTrackedRequests =
            activeRequestsRef.current.size > 0;

          const finalResources = window.performance.getEntriesByType(
            'resource'
          ) as PerformanceResourceTiming[];
          const finalNow = performance.now();
          const finalPendingRequests = finalResources.filter((resource) => {
            return (
              resource.responseEnd === 0 &&
              resource.startTime > 0 &&
              resource.startTime > finalNow - 60000
            );
          });

          const finalQueries = queryClient.getQueryCache().getAll();
          const finalHasActiveQueries = finalQueries.some(
            (query) => query.state.fetchStatus === 'fetching'
          );

          // Re-query chart containers to avoid stale references
          const finalChartContainers = document.querySelectorAll(
            '.highcharts-container'
          );
          const finalChartsReady =
            finalChartContainers.length === 0 ||
            Array.from(finalChartContainers).every((container) => {
              const svg = container.querySelector('svg');
              return svg && svg.children.length > 0;
            });

          // Final check for images
          const finalImages = document.querySelectorAll('img');
          const finalImagesLoaded = Array.from(finalImages).every(
            (img) => img.complete
          );

          const finalDocumentReady = document.readyState === 'complete';

          if (
            !finalHasActiveTrackedRequests &&
            finalPendingRequests.length === 0 &&
            !finalHasActiveQueries &&
            finalChartsReady &&
            finalImagesLoaded &&
            finalDocumentReady
          ) {
            if (!isNetworkIdleStateRef.current) {
              isNetworkIdleStateRef.current = true;
              setIsNetworkIdle(true);

              // Set the window flag that Gotenberg waits for
              window.reportPdfGeneration = 'ready';
            }
          } else {
            // If new requests started, restart the check
            checkNetworkIdle();
          }
        }, timeout);
      } else {
        // If there are pending operations, check again after a short delay
        if (isNetworkIdleStateRef.current) {
          isNetworkIdleStateRef.current = false;
          setIsNetworkIdle(false);
          window.reportPdfGeneration = 'notReady';
        }
        timeoutId = setTimeout(checkNetworkIdle, 200);
      }
    };

    // Reset state before starting the check
    isMountedRef.current = true;
    isNetworkIdleStateRef.current = false;
    setIsNetworkIdle(false);
    activeRequestsRef.current.clear();
    window.reportPdfGeneration = 'notReady';

    // Start checking after a short delay to allow interceptors to be set up
    setTimeout(() => {
      checkNetworkIdle();
    }, 100);

    // Also set up an interval to periodically check chart rendering
    const chartCheckInterval = setInterval(() => {
      if (!isNetworkIdleStateRef.current && isMountedRef.current) {
        checkNetworkIdle();
      }
    }, 500);

    return () => {
      // Mark as unmounted to prevent any callbacks from running
      isMountedRef.current = false;

      // Cleanup timeouts on unmount to prevent memory leaks
      clearTimeout(timeoutId);
      clearInterval(chartCheckInterval);

      // Restore original functions to prevent affecting other parts of the application
      // This is critical since we're modifying global prototypes
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
      if (originalXHROpenRef.current) {
        XMLHttpRequest.prototype.open = originalXHROpenRef.current;
      }
      if (originalXHRSendRef.current) {
        XMLHttpRequest.prototype.send = originalXHRSendRef.current;
      }

      // Reset the window flag on cleanup
      window.reportPdfGeneration = 'notReady';
    };
  }, [timeout, queryClient]);

  // Return true if network is idle, false otherwise
  return isNetworkIdle;
};
